import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Button, IconButton, Checkbox, Snackbar, Alert, CircularProgress, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../api';

const F = (v) => (v === null || v === undefined ? '' : v);

// Колір рядка за тривалістю (читабельність): 1м нейтральний, 3м синій, 6м зелений, 12м золотий
const DUR_BG = { 1: 'transparent', 3: 'rgba(33,150,243,0.14)', 6: 'rgba(76,175,80,0.14)', 12: 'rgba(255,193,7,0.16)' };
const DUR_DOT = { 1: '#666', 3: '#2196f3', 6: '#4caf50', 12: '#ffc107' };
const durBg = (dm) => DUR_BG[parseInt(dm)] || 'transparent';
const durDot = (dm) => DUR_DOT[parseInt(dm)] || '#666';

// Phase 4.2 (pricing): редактор пакетів/тарифів — Postgres = SoT. Ціни/тривалості/badge редагуються тут.
export default function PackagesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [filterDur, setFilterDur] = useState(null); // null = всі тривалості
  const [rates, setRates] = useState({ uah: null, usd: null }); // [D-656] курс лише для підпису; ціни рахує сервер (calc)
  const tok = localStorage.getItem('dashboard_token');

  // [МЕДІА УНІКАЛІЗАТОР] ціна € вводиться кроком 0.50 (як і було); [D-656] ₴ і $ / USDT рахує сервер калькулятором
  // (вгору до цілої гривні / 0.50 $ - рівно стільки заплатить юзер). Змінили € і ще не зберегли - показуємо «збережіть».
  const roundTo = (v, step) => Math.round((parseFloat(v) || 0) / step) * step;
  const conv = (r) => (String(r.price_eur) === String(r._eur0) ? { uah: r.uah_calc, usd: r.usd_calc } : null);

  const load = () => {
    setLoading(true);
    api.get('/packages').then(r => { setRows((r.data || []).map(x => ({ ...x, _eur0: x.price_eur }))); setLoading(false); })
      .catch(() => { setMsg({ s: 'error', t: 'Помилка завантаження' }); setLoading(false); });
    fetch('/api/referral/settings', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : {})).then(d => setRates({ uah: parseFloat(d.eur_uah_rate) || null, usd: parseFloat(d.eur_usd_rate) || null })).catch(() => {});
  };
  useEffect(load, []);

  const setField = (id, key, val) => setRows(rs => rs.map(r => r.id === id ? { ...r, [key]: val } : r));

  // ціна вводиться ЛИШЕ в EUR (округл. 0.50); [D-656] UAH рахує сервер (calc.uah_price) при збереженні
  const params = (r) => {
    const eur = roundTo(r.price_eur, 0.5);
    return {
      name: r.name || '', daily_limit: parseInt(r.daily_limit) || 0, can_publish: r.can_publish ? 1 : 0,
      price_eur: eur, price_uah: parseFloat(r.price_uah) || 0,
      duration_months: parseInt(r.duration_months) || 1, badge: r.badge || '', sort_order: parseInt(r.sort_order) || 0,
      kind: (r.kind === 'pcapp' || r.kind === 'tokens') ? r.kind : 'media',
      tokens_amount: parseInt(r.tokens_amount) || 0,
    };
  };

  const recomputeAll = async () => {
    if (!window.confirm('Перерахувати UAH-ціни ВСІХ пакетів за курсом 1€=' + (rates.uah || '?') + '₴?\n(EUR не зміниться, оновляться лише гривневі ціни)')) return;
    try { const r = await api.post('/packages/recompute-prices'); setMsg({ s: 'success', t: '✅ Оновлено ' + r.data.changed + ' пакетів за курсом ' + r.data.rate }); load(); }
    catch (e) { setMsg({ s: 'error', t: '❌ ' + (e.response?.data?.detail || e.message) }); }
  };

  const save = async (r) => {
    try { await api.put(`/packages/${r.id}`, null, { params: params(r) }); setMsg({ s: 'success', t: `✅ Збережено #${r.id}` }); load(); }
    catch (e) { setMsg({ s: 'error', t: '❌ ' + (e.response?.data?.detail || e.message) }); }
  };

  const addRow = async () => {
    const so = rows.length ? Math.max(...rows.map(r => parseInt(r.sort_order) || 0)) + 1 : 1;
    const isPc = filterDur === 'pcapp';
    const isTok = filterDur === 'tokens';
    const np = isPc
      ? { name: 'PC Unlimited 1 міс', daily_limit: 0, can_publish: 0, price_eur: 0, price_uah: 0, duration_months: 1, badge: '', sort_order: so, kind: 'pcapp' }
      : isTok
      ? { name: '🎟 100 токенів', daily_limit: 0, can_publish: 0, price_eur: 9, price_uah: 0, duration_months: 1, badge: '', sort_order: so, kind: 'tokens', tokens_amount: 100 }
      : { name: 'Новий пакет', daily_limit: 3, can_publish: 0, price_eur: 0, price_uah: 0, duration_months: 1, badge: '', sort_order: so, kind: 'media' };
    try { await api.post('/packages', null, { params: np }); setMsg({ s: 'success', t: '✅ Додано' }); load(); }
    catch (e) { setMsg({ s: 'error', t: '❌ ' + (e.response?.data?.detail || e.message) }); }
  };

  const del = async (id) => {
    if (!window.confirm('Видалити пакет #' + id + '?')) return;
    try { await api.delete(`/packages/${id}`); setMsg({ s: 'success', t: '🗑 Видалено' }); load(); }
    catch (e) { setMsg({ s: 'error', t: '❌ ' + (e.response?.data?.detail || e.message) }); }
  };

  const cellSx = { '& .MuiInputBase-input': { color: '#fff', fontSize: 13, padding: '6px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ color: '#fff' }}>💵 Пакети / Тарифи</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={addRow} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Додати пакет</Button>
        <Tooltip title="Перерахувати UAH-ціни всіх пакетів за поточним курсом (EUR не змінюється)">
          <Button variant="outlined" onClick={recomputeAll} sx={{ borderColor: '#ff9800', color: '#ff9800' }}>♻️ Перерахувати всі UAH</Button>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.4, borderRadius: 1, bgcolor: 'rgba(255,152,0,0.08)' }}>
          <Typography variant="caption" sx={{ color: '#ffb74d' }}>📡 Курс: 1€ = {rates.uah}₴ · {rates.usd}$</Typography>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 2 }}>
        Вводь ціну <b>ЛИШЕ в EUR</b> (округл. до 0.50€) — UAH/USD/USDT рахуються авто за живим курсом (UAH→1₴, USD/USDT→0.5). Колонка «Ціни (авто)» — лише показ; при «Зберегти» UAH записується за курсом. <b>duration_months</b>: 1/3/6/12.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Box onClick={() => setFilterDur(null)} sx={{ display: 'flex', alignItems: 'center', px: 1.2, py: 0.3, borderRadius: 1, cursor: 'pointer', border: filterDur === null ? '2px solid var(--ac1, #7c4dff)' : '1px solid #333' }}>
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: filterDur === null ? 'bold' : 'normal' }}>Всі</Typography>
        </Box>
        {[[1, '1 міс'], [3, '3 міс'], [6, '6 міс'], [12, '12 міс']].map(([d, l]) => (
          <Box key={d} onClick={() => setFilterDur(filterDur === d ? null : d)} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.3, borderRadius: 1, cursor: 'pointer', bgcolor: durBg(d), border: filterDur === d ? '2px solid ' + durDot(d) : '1px solid #333' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: durDot(d) }} />
            <Typography variant="caption" sx={{ color: '#ddd', fontWeight: filterDur === d ? 'bold' : 'normal' }}>{l}</Typography>
          </Box>
        ))}
        <Box onClick={() => setFilterDur(filterDur === 'pcapp' ? null : 'pcapp')} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.3, borderRadius: 1, cursor: 'pointer', bgcolor: 'rgba(0,188,212,0.12)', border: filterDur === 'pcapp' ? '2px solid #00bcd4' : '1px solid #00bcd4' }}>
          <Typography variant="caption" sx={{ color: '#00bcd4', fontWeight: filterDur === 'pcapp' ? 'bold' : 'normal' }}>🖥 PC Unlimited</Typography>
        </Box>
        <Box onClick={() => setFilterDur(filterDur === 'tokens' ? null : 'tokens')} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.3, borderRadius: 1, cursor: 'pointer', bgcolor: 'rgba(0,229,160,0.12)', border: filterDur === 'tokens' ? '2px solid #00e5a0' : '1px solid #00e5a0' }}>
          <Typography variant="caption" sx={{ color: '#00e5a0', fontWeight: filterDur === 'tokens' ? 'bold' : 'normal' }}>🎟 Токени</Typography>
        </Box>
      </Box>
      {loading ? <CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }} /> :
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['ID', 'Назва', 'Відео/день', 'Публ.', 'Токенів', 'EUR', 'Ціни (авто)', 'Міс', 'Badge', 'Порядок', ''].map(h =>
                <TableCell key={h} sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {rows.filter(r => (filterDur === 'pcapp' || filterDur === 'tokens')
                ? ((r.kind || 'media') === filterDur)
                : ((r.kind || 'media') !== 'pcapp' && (r.kind || 'media') !== 'tokens'
                   && (filterDur === null || parseInt(r.duration_months) === filterDur))).map(r => (
                <TableRow key={r.id} sx={{ bgcolor: durBg(r.duration_months), '& td': { borderColor: '#262640' } }}>
                  <TableCell sx={{ color: '#888' }}>{r.id}</TableCell>
                  <TableCell><TextField value={F(r.name)} onChange={e => setField(r.id, 'name', e.target.value)} sx={{ ...cellSx, width: 160 }} size="small" /></TableCell>
                  <TableCell><TextField type="number" value={F(r.daily_limit)} onChange={e => setField(r.id, 'daily_limit', e.target.value)} sx={{ ...cellSx, width: 70 }} size="small" /></TableCell>
                  <TableCell><Checkbox checked={!!r.can_publish} onChange={e => setField(r.id, 'can_publish', e.target.checked ? 1 : 0)} sx={{ color: 'var(--ac1, #7c4dff)' }} /></TableCell>
                  <TableCell>
                    {(r.kind || 'media') === 'tokens'
                      ? <TextField type="number" value={F(r.tokens_amount)} onChange={e => setField(r.id, 'tokens_amount', e.target.value)} sx={{ ...cellSx, width: 80, '& .MuiInputBase-input': { color: '#00e5a0', fontWeight: 'bold', fontSize: 13, padding: '6px' } }} size="small" />
                      : <span style={{ color: '#555' }}>—</span>}
                  </TableCell>
                  <TableCell><TextField type="number" value={F(r.price_eur)} onChange={e => setField(r.id, 'price_eur', e.target.value)} sx={{ ...cellSx, width: 80 }} size="small"
                    InputProps={{ startAdornment: <span style={{ color: '#888', fontSize: 12, marginRight: 2 }}>€</span> }} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => { const c = conv(r); if (!c) return (
                      <Typography sx={{ fontSize: 10.5, color: '#ff9800' }}>💾 Збережіть - ₴ і $ перерахуються</Typography>
                    ); return (
                      <Box sx={{ lineHeight: 1.25 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 'bold' }}>
                          <span style={{ color: '#ffd54f' }}>₴{c.uah == null ? '?' : c.uah}</span> <span style={{ color: '#555' }}>·</span> <span style={{ color: '#81c784' }}>${c.usd == null ? '?' : c.usd}</span>
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#90caf9' }}>₮ {c.usd == null ? '?' : c.usd} USDT/USDC</Typography>
                        {c.uah != null && String(parseFloat(r.price_uah) || 0) !== String(c.uah) && (
                          <Typography sx={{ fontSize: 9, color: '#ff9800' }}>збережено ₴{parseFloat(r.price_uah) || 0} → Зберегти</Typography>
                        )}
                      </Box>
                    ); })()}
                  </TableCell>
                  <TableCell><TextField type="number" value={F(r.duration_months)} onChange={e => setField(r.id, 'duration_months', e.target.value)} sx={{ ...cellSx, width: 60, '& .MuiInputBase-input': { color: durDot(r.duration_months), fontWeight: 'bold', fontSize: 13, padding: '6px' } }} size="small" /></TableCell>
                  <TableCell><TextField value={F(r.badge)} onChange={e => setField(r.id, 'badge', e.target.value)} sx={{ ...cellSx, width: 150 }} size="small" /></TableCell>
                  <TableCell><TextField type="number" value={F(r.sort_order)} onChange={e => setField(r.id, 'sort_order', e.target.value)} sx={{ ...cellSx, width: 60 }} size="small" /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Зберегти"><IconButton onClick={() => save(r)} sx={{ color: '#4caf50' }}><SaveIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Видалити"><IconButton onClick={() => del(r.id)} sx={{ color: '#f44336' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>}
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {msg ? <Alert severity={msg.s} onClose={() => setMsg(null)}>{msg.t}</Alert> : null}
      </Snackbar>
    </Box>
  );
}
